import { useEffect, useMemo, useRef } from "react";
import classes from "./DateChooser.module.css"
import 'react-datepicker/dist/react-datepicker.css'
import { useBooking } from "../../../store/bookingStore";
import { parseLocalDateString } from "../../../util/parseLocalDate";

import DatePicker, { registerLocale, setDefaultLocale } from "react-datepicker"
import { isSameDay, startOfDay, endOfDay, isBefore, startOfToday, isToday } from "date-fns";
import { de } from 'date-fns/locale';
import {
  apiDayToJsDay,
  formatLocalDate,
  getBookingDurationHours,
  getBlockedDateIntervals as getStudioBlockedDateIntervals,
  hasBusyIntervalOnDate,
  hasBookableTimeSlot,
  hasSufficientAvailabilityForDate,
  isDateFullyBlocked as isStudioDateFullyBlocked,
} from "../../../util/studioAvailability";

// Configure DatePicker to use German locale so weeks start on Monday
registerLocale('de', de);
setDefaultLocale('de');

const DatePickerWrapper = ({ children }) => (
  <div className={classes.datePickerWrapper}>{children}</div>
);

export const DateChooser = ({ nextSlide, bookingStudioData, fromDesktop=false }) => {
  const { bookingData, updateBookingData } = useBooking();

  // Derive the selected date directly from the booking store so that the
  // calendar can never get out of sync with `bookingData.date`. Whenever the
  // store is cleared (e.g. on package change or when leaving the booking
  // flow), the calendar immediately reflects the empty state and the
  // auto-pick effect below re-runs to choose the next available date.
  const selectedDate = useMemo(() => {
    return parseLocalDateString(bookingData.date);
  }, [bookingData.date]);

  const lastDateChangeRef = useRef({ key: '', at: 0 });
  const bookingDurationHours = getBookingDurationHours(bookingStudioData, bookingData);

  // Auto-pick or repair the selected date whenever package/hour duration changes.
  useEffect(() => {
    if (!bookingStudioData) return;

    const currentDate = parseLocalDateString(bookingData.date);
    if (
      currentDate &&
      hasBookableTimeSlot(currentDate, bookingStudioData, {
        durationHours: bookingDurationHours,
      })
    ) {
      return;
    }

    const today = new Date();
    let nextAvailableDate = null;

    for (let i = 0; i <= 30; i++) {
      const dateToCheck = new Date(today);
      dateToCheck.setDate(today.getDate() + i);

      if (
        hasBookableTimeSlot(dateToCheck, bookingStudioData, {
          durationHours: bookingDurationHours,
        })
      ) {
        nextAvailableDate = dateToCheck;
        break;
      }
    }

    if (nextAvailableDate) {
      updateBookingData('date', formatLocalDate(nextAvailableDate));
    } else if (bookingData.date) {
      updateBookingData('date', '');
    }
  }, [bookingStudioData, bookingData.date, bookingDurationHours, updateBookingData]);

  const handleDateChange = (date) => {
    if (!date) return;

    const formattedDate = formatLocalDate(date);
    const now = Date.now();
    const isDuplicate =
      lastDateChangeRef.current.key === formattedDate &&
      now - lastDateChangeRef.current.at < 300;

    if (isDuplicate) return;

    lastDateChangeRef.current = { key: formattedDate, at: now };
    updateBookingData('date', formattedDate);

    // Auto-close when in desktop mode after selecting a date
    if (fromDesktop) {
      nextSlide();
    }
  };

  const getBlockedDateIntervals = () => getStudioBlockedDateIntervals(bookingStudioData);
  
  // Helper function to check if a specific day has insufficient time slots
  const hasSufficientTimeSlots = (dayOfWeekJs) => {
    const referenceSunday = new Date(2024, 0, 7);
    const referenceDate = new Date(referenceSunday);
    referenceDate.setDate(referenceSunday.getDate() + dayOfWeekJs);
    return hasSufficientAvailabilityForDate(
      referenceDate,
      bookingStudioData,
      bookingDurationHours
    );
  };

  const isPartiallyAvailable = (date) => {
    if (!bookingStudioData) return false;
    
    // Don't show indicators for past dates (except today)
    if (isBefore(date, startOfToday()) && !isToday(date)) return false;
    
    // First check if the date is fully blocked by blocked intervals
    // If it is fully blocked, don't show partial availability indicator
    if (isDateFullyBlocked(date)) {
      return false;
    }
    
    const dayOfWeekJs = date.getDay();

    // Check if day has insufficient time slots for minimum booking duration
    // If it doesn't have sufficient time slots, don't show partial indicator
    if (!hasSufficientTimeSlots(dayOfWeekJs)) {
      return false;
    }
    
    // Check if day has partial availability restrictions
    const hasPartialAvailability = bookingStudioData.availabilities?.items?.some(item => 
      apiDayToJsDay(item.dayOfWeek) === dayOfWeekJs && 
      (item.startTime !== "00:00" || item.endTime !== "23:59")
    );
    
    // Check if day is the start or end day of a partial block period
    const isPartialBlockPeriodDay = bookingStudioData.blockedTimes?.items?.some(item => {
      if (item.isFullDay) return false;
      
      const startDate = new Date(item.startDateTime);
      const endDate = new Date(item.endDateTime);
      
      // Only the start day and end day of a partial block period are partially available
      return isSameDay(date, startDate) || isSameDay(date, endDate);
    });
    
    // Check if day has bookings
    const hasBookings = hasBusyIntervalOnDate(date, bookingStudioData);
    
    return hasPartialAvailability || isPartialBlockPeriodDay || hasBookings;
  };

  // Helper function to check if a date is fully blocked
  const isDateFullyBlocked = (date) => isStudioDateFullyBlocked(date, bookingStudioData);

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (!filterDate(new Date())) {
      return tomorrow; // Use tomorrow as min date if today is unavailable
    }
    
    return new Date(); // Otherwise use today
  };
  
  const filterDate = (date) => {
    return hasBookableTimeSlot(date, bookingStudioData, {
      durationHours: bookingDurationHours,
    });
  };

  const getCombinedBlockedIntervals = () => {
    const intervals = getBlockedDateIntervals();
    
    // If today is unavailable but not caught by other filters, explicitly add it
    if (isToday(new Date()) && !filterDate(new Date())) {
      const today = new Date();
      intervals.push({
        start: startOfDay(today),
        end: endOfDay(today)
      });
    }
    
    return intervals;
  };

  const renderDayContents = (day, date) => {
    const isPartial = isPartiallyAvailable(date);

    return (
      <div className={classes.verfugbarDay}>
        <span>{day}</span>
        {isPartial && (
          <svg xmlns="http://www.w3.org/2000/svg" width="19" height="5" viewBox="0 0 19 5" fill="none">
            <rect x="0.5" y="0.926758" width="17.2876" height="3" rx="1.5" stroke="#B1B6BB"/>
            <rect x="0.5" y="0.926758" width="8.87109" height="3" rx="1.5" fill="#B1B6BB" stroke="#B1B6BB"/>
          </svg>
        )}
      </div>
    );
  };

  const datePickerWrapperClass = fromDesktop ? `${classes.datePickerWrapper} ${classes.desktopDatePicker}` : classes.datePickerWrapper;

  return (
    <div 
      className={classes.outsideContainer} 
      style={{ height: fromDesktop ? 'fit-content' : '', 
      justifyContent: fromDesktop ? 'flex-start' : 'center',
      paddingBottom: fromDesktop ? '0px' : '120px' 
    }}>
      <div className={classes.weekContainer}> 
        <div className={classes.weekDay}>
          <span>Mo.</span>
        </div>
        <div className={classes.weekDay}>
          <span>Di.</span>
        </div>
        <div className={classes.weekDay}>
          <span>Mi.</span>
        </div>
        <div className={classes.weekDay}>
          <span>Do.</span>
        </div>
        <div className={classes.weekDay}>
          <span>Fr.</span>
        </div>
        <div className={classes.weekDay}>
          <span>Sa.</span>
        </div>
        <div className={classes.weekDay}>
          <span>So.</span>
        </div>
      </div>
      <div className={classes.container}>
        <div className={datePickerWrapperClass}>
          <DatePicker
            selected={selectedDate}
            dateFormat="dd.MM.yyyy"
            inline
            locale="de"
            minDate={getMinDate()}
            monthsShown={fromDesktop ? 1 : 4}
            renderDayContents={renderDayContents}
            onChange={handleDateChange}
            excludeDateIntervals={getCombinedBlockedIntervals()}
            filterDate={filterDate}
          />
        </div>
      </div>
      <div className={classes.weiterButtonContainer} style={{display: fromDesktop ? 'none' : 'block'}}>
        <button disabled={!selectedDate} className={classes.weiterButton} onClick={nextSlide}>Weiter</button>
      </div>
    </div>
  );
};

export default DateChooser;
